import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Loader2, Sparkles, History,
  Download, FileText, Printer, Copy, Eye, Pencil, PenLine, Upload, Check,
} from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { logError } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import ContractAiDrawer from "./ContractAiDrawer";
import { STATUSES, STATUS_STYLE, fmtDate } from "./util";

export default function ContractEditor({ contractId, canDocx, onBack, onChanged }) {
  const [c, setC] = useState(null);
  const [sections, setSections] = useState([]);
  const [title, setTitle] = useState("");
  const [meta, setMeta] = useState({ tags: "", counterparty: "", governing_state: "", effective_date: "", start_date: "", end_date: "", expiration_date: "", renewal_date: "" });
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saveError, setSaveError] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const sigFileRef = useRef(null);
  const firstLoad = useRef(true);

  const load = useCallback(() => {
    api.get(`/contracts/${contractId}`).then(({ data }) => {
      setC(data); setSections(data.sections || []); setTitle(data.title || "");
      setMeta({
        tags: (data.tags || []).join(", "), counterparty: data.counterparty || "",
        governing_state: data.governing_state || "", effective_date: data.effective_date || "",
        start_date: data.start_date || "", end_date: data.end_date || "",
        expiration_date: data.expiration_date || "", renewal_date: data.renewal_date || "",
      });
    }).catch(() => toast.error("Could not load contract"));
  }, [contractId]);
  useEffect(() => { load(); }, [load]);

  // Debounced autosave (no new version on autosave).
  useEffect(() => {
    if (!c) return;
    if (firstLoad.current) { firstLoad.current = false; return; }
    const t = setTimeout(async () => {
      setSaving(true);
      try {
        const { data } = await api.put(`/contracts/${contractId}`, {
          title, sections, create_version: false,
          tags: meta.tags.split(",").map((x) => x.trim()).filter(Boolean),
          counterparty: meta.counterparty, governing_state: meta.governing_state,
          effective_date: meta.effective_date || null, start_date: meta.start_date || null,
          end_date: meta.end_date || null, expiration_date: meta.expiration_date || null,
          renewal_date: meta.renewal_date || null,
        });
        setC(data); setSavedAt(new Date()); setSaveError(false); onChanged?.();
      } catch (e) { setSaveError(true); logError("contracts", "Autosave failed", { contractId }); }
      finally { setSaving(false); }
    }, 1100);
    return () => clearTimeout(t);
  }, [title, sections, meta, contractId]); // eslint-disable-line

  if (!c) return <div className="flex justify-center py-24 text-[#71717A]"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const setSec = (i, k, v) => setSections((s) => s.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const move = (i, dir) => setSections((s) => {
    const j = i + dir; if (j < 0 || j >= s.length) return s;
    const n = [...s]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const addClause = (body = "") => setSections((s) => [...s, { id: `sec_${Math.random().toString(36).slice(2, 10)}`, title: "New Clause", body }]);
  const removeSec = (i) => setSections((s) => s.filter((_, idx) => idx !== i));

  const saveVersion = async () => {
    try {
      await api.put(`/contracts/${contractId}`, { title, sections, create_version: true });
      toast.success("Version saved"); load();
    } catch { toast.error("Could not save version"); }
  };
  const setStatus = async (status) => {
    try { const { data } = await api.patch(`/contracts/${contractId}/status`, { status }); setC(data); onChanged?.(); toast.success(`Status: ${status}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const sign = async () => {
    const name = window.prompt("Full name of the signer:");
    if (!name) return;
    try { const { data } = await api.post(`/contracts/${contractId}/sign`, { signer_name: name }); setC(data); onChanged?.(); toast.success("Marked as signed"); }
    catch { toast.error("Could not sign"); }
  };
  const uploadSig = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const form = new FormData(); form.append("file", file);
    try { const { data } = await api.post(`/contracts/${contractId}/signature-upload`, form, { headers: { "Content-Type": "multipart/form-data" } }); setC(data); onChanged?.(); toast.success("Signed copy uploaded"); }
    catch { toast.error("Upload failed"); }
    finally { if (sigFileRef.current) sigFileRef.current.value = ""; }
  };
  const duplicate = async () => {
    try { const { data } = await api.post(`/contracts/${contractId}/duplicate`); toast.success("Duplicated"); onChanged?.(); onBack(data); }
    catch { toast.error("Could not duplicate"); }
  };
  const openVersions = async () => {
    setVersionsOpen(true);
    api.get(`/contracts/${contractId}/versions`).then(({ data }) => setVersions(data.versions)).catch(() => {});
  };
  const restore = async (v) => {
    try { await api.post(`/contracts/${contractId}/versions/${v}/restore`); toast.success(`Restored v${v}`); setVersionsOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Restore failed"); }
  };
  const dl = (fmt) => window.open(`${API}/contracts/${contractId}/${fmt}`, "_blank");

  return (
    <div className="fv-fade-up" data-testid="contract-editor">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <button onClick={() => onBack()} data-testid="editor-back" className="flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-white"><ArrowLeft className="w-4 h-4" /> Contracts</button>
        <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_STYLE[c.status]}`} data-testid="editor-status-badge">{c.status}</span>
        <span className="text-xs text-[#52525B]" data-testid="editor-save-state">{saving ? "Saving…" : saveError ? "Save failed — retrying on next edit" : savedAt ? "All changes saved" : `v${c.version}`}</span>
        <div className="ml-auto flex items-center gap-1.5">
          <button onClick={() => setAiOpen(true)} data-testid="editor-ai-btn" title="AI Assistant" className="h-9 px-3 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-sm flex items-center gap-1.5 hover:bg-[#D4AF37]/20"><Sparkles className="w-4 h-4" /> AI</button>
          <button onClick={() => setPreview((p) => !p)} data-testid="editor-preview-toggle" title="Preview" className="w-9 h-9 rounded-full bg-white/5 border border-white/15 text-[#A1A1AA] hover:text-white flex items-center justify-center">{preview ? <Pencil className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
          <button onClick={openVersions} data-testid="editor-versions-btn" title="Version history" className="w-9 h-9 rounded-full bg-white/5 border border-white/15 text-[#A1A1AA] hover:text-white flex items-center justify-center"><History className="w-4 h-4" /></button>
          <button onClick={() => dl("pdf")} data-testid="editor-download-pdf" title="Download PDF" className="w-9 h-9 rounded-full bg-white/5 border border-white/15 text-[#A1A1AA] hover:text-white flex items-center justify-center"><Download className="w-4 h-4" /></button>
          <button onClick={() => canDocx ? dl("docx") : toast.error("DOCX export requires the Growth plan or higher.")} data-testid="editor-download-docx" title="Download DOCX" className="w-9 h-9 rounded-full bg-white/5 border border-white/15 text-[#A1A1AA] hover:text-white flex items-center justify-center"><FileText className="w-4 h-4" /></button>
          <button onClick={() => dl("pdf")} data-testid="editor-print" title="Print" className="w-9 h-9 rounded-full bg-white/5 border border-white/15 text-[#A1A1AA] hover:text-white flex items-center justify-center"><Printer className="w-4 h-4" /></button>
          <button onClick={duplicate} data-testid="editor-duplicate" title="Duplicate" className="w-9 h-9 rounded-full bg-white/5 border border-white/15 text-[#A1A1AA] hover:text-white flex items-center justify-center"><Copy className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_300px] gap-6">
        {/* Document */}
        <div className="min-w-0">
          <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="editor-title"
            className="w-full bg-transparent border-b border-white/10 pb-2 mb-5 font-heading text-2xl text-white focus:outline-none focus:border-[#D4AF37]/50" />
          {preview ? (
            <div className="rounded-2xl border border-white/8 bg-[#0f0f11] p-6 space-y-4" data-testid="editor-preview">
              {sections.map((s, i) => (
                <div key={s.id}>
                  <h3 className="font-heading text-sm text-[#D4AF37] mb-1">{i + 1}. {s.title}</h3>
                  <p className="text-sm text-[#D4D4D8] whitespace-pre-wrap leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3" data-testid="editor-sections">
              {sections.map((s, i) => (
                <div key={s.id} data-testid={`section-${i}`} className="rounded-xl border border-white/8 bg-[#121214] p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-[#52525B] shrink-0">{i + 1}.</span>
                    <input value={s.title} onChange={(e) => setSec(i, "title", e.target.value)} data-testid={`section-title-${i}`}
                      className="flex-1 bg-transparent text-sm font-medium text-white focus:outline-none" />
                    <button onClick={() => move(i, -1)} data-testid={`section-up-${i}`} className="w-7 h-7 rounded-md text-[#71717A] hover:text-white hover:bg-white/10 flex items-center justify-center"><ChevronUp className="w-4 h-4" /></button>
                    <button onClick={() => move(i, 1)} data-testid={`section-down-${i}`} className="w-7 h-7 rounded-md text-[#71717A] hover:text-white hover:bg-white/10 flex items-center justify-center"><ChevronDown className="w-4 h-4" /></button>
                    <button onClick={() => removeSec(i)} data-testid={`section-remove-${i}`} className="w-7 h-7 rounded-md text-[#71717A] hover:text-red-400 hover:bg-white/10 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <textarea value={s.body} onChange={(e) => setSec(i, "body", e.target.value)} data-testid={`section-body-${i}`} rows={Math.min(10, Math.max(3, (s.body || "").split("\n").length + 1))}
                    className="w-full bg-[#0d0d0f] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#D4D4D8] focus:outline-none focus:border-[#D4AF37]/40 resize-y leading-relaxed" />
                </div>
              ))}
              <div className="flex gap-2">
                <Button onClick={() => addClause()} data-testid="editor-add-clause" variant="outline" className="border-white/15 bg-transparent text-white rounded-full"><Plus className="w-4 h-4 mr-1" /> Add clause</Button>
                <Button onClick={saveVersion} data-testid="editor-save-version" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full hover:opacity-90"><Check className="w-4 h-4 mr-1" /> Save version</Button>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-4" data-testid="editor-sidebar">
          <div className="rounded-xl border border-white/8 bg-[#121214] p-4 space-y-3">
            <div>
              <label className="text-xs text-[#A1A1AA] mb-1.5 block">Status</label>
              <Select value={c.status} onValueChange={setStatus}>
                <SelectTrigger className="bg-[#0d0d0f] border-white/10 text-white" data-testid="editor-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1C1C1F] border-white/10 text-white">{STATUSES.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/10">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <MetaField label="Other party" v={meta.counterparty} onC={(v) => setMeta((m) => ({ ...m, counterparty: v }))} testid="editor-counterparty" />
            <MetaField label="Governing state" v={meta.governing_state} onC={(v) => setMeta((m) => ({ ...m, governing_state: v }))} testid="editor-governing-state" />
            <MetaField label="Tags (comma separated)" v={meta.tags} onC={(v) => setMeta((m) => ({ ...m, tags: v }))} testid="editor-tags" />
          </div>

          <div className="rounded-xl border border-white/8 bg-[#121214] p-4 space-y-3">
            <p className="text-xs uppercase tracking-wider text-[#52525B]">Key dates</p>
            <MetaField label="Effective" type="date" v={meta.effective_date?.slice(0,10)} onC={(v) => setMeta((m) => ({ ...m, effective_date: v }))} testid="editor-effective-date" />
            <MetaField label="Expiration" type="date" v={meta.expiration_date?.slice(0,10)} onC={(v) => setMeta((m) => ({ ...m, expiration_date: v }))} testid="editor-expiration-date" />
            <MetaField label="Renewal reminder" type="date" v={meta.renewal_date?.slice(0,10)} onC={(v) => setMeta((m) => ({ ...m, renewal_date: v }))} testid="editor-renewal-date" />
          </div>

          <div className="rounded-xl border border-white/8 bg-[#121214] p-4 space-y-2.5" data-testid="signature-panel">
            <p className="text-xs uppercase tracking-wider text-[#52525B]">Signature</p>
            {c.signed_by ? (
              <div className="text-xs text-[#D4AF37] flex items-center gap-1.5"><Check className="w-4 h-4" /> Signed by {c.signed_by} · {fmtDate(c.signed_at)}</div>
            ) : (
              <p className="text-xs text-[#71717A]">Signature-ready. Mark as signed or upload a signed copy. (E-signature providers can be connected later.)</p>
            )}
            <Button onClick={() => setStatus("Ready for Signature")} data-testid="sig-mark-ready" variant="outline" className="w-full border-white/15 bg-transparent text-white rounded-full text-xs"><PenLine className="w-4 h-4 mr-1" /> Mark ready for signature</Button>
            <Button onClick={sign} data-testid="sig-mark-signed" className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full text-xs"><Check className="w-4 h-4 mr-1" /> Mark as signed</Button>
            <input ref={sigFileRef} type="file" onChange={uploadSig} className="hidden" data-testid="sig-file-input" accept=".pdf,.png,.jpg,.jpeg" />
            <Button onClick={() => sigFileRef.current?.click()} data-testid="sig-upload-btn" variant="outline" className="w-full border-white/15 bg-transparent text-[#A1A1AA] rounded-full text-xs"><Upload className="w-4 h-4 mr-1" /> Upload signed copy</Button>
          </div>
        </aside>
      </div>

      <ContractAiDrawer contract={c} open={aiOpen} onClose={() => setAiOpen(false)} onInsertClause={(text) => addClause(text)} />

      <Dialog open={versionsOpen} onOpenChange={setVersionsOpen}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md" data-testid="versions-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Version history</DialogTitle>
            <DialogDescription className="text-[#71717A]">Restore an earlier draft of this contract.</DialogDescription></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="versions-list">
            {versions.map((v) => (
              <div key={v.version} className={`flex items-center justify-between rounded-xl border p-3 ${v.is_current ? "border-[#D4AF37]/25 bg-[#151310]" : "border-white/8 bg-[#0d0d0f]"}`} data-testid={`version-row-${v.version}`}>
                <div className="min-w-0">
                  <div className="text-sm text-white flex items-center gap-2">v{v.version}{v.is_current && <span className="text-[9px] uppercase tracking-wider text-[#D4AF37]">Current</span>}</div>
                  <div className="text-xs text-[#71717A] truncate">{v.title} · {fmtDate(v.created_at)}</div>
                </div>
                {!v.is_current && <button onClick={() => restore(v.version)} data-testid={`version-restore-${v.version}`} className="text-xs text-[#D4AF37] hover:underline shrink-0">Restore</button>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetaField({ label, v, onC, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs text-[#A1A1AA] mb-1.5 block">{label}</label>
      <input type={type} value={v || ""} onChange={(e) => onC(e.target.value)} data-testid={testid}
        className="w-full bg-[#0d0d0f] border border-white/10 rounded-lg px-3 h-10 text-sm text-white focus:outline-none focus:border-[#D4AF37]/40" />
    </div>
  );
}
